import React, { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import reservationStore from '../../stores/ReservationStore';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { toast } from 'react-toastify';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const ReservationModal: React.FC<Props> = observer(({ isOpen, onClose }) => {
  const [step, setStep] = useState<1 | 2>(1);
  const [selection, setSelection] = useState<Date | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(600); // 10 minutes
  const [partySizeInput, setPartySizeInput] = useState<string>('2');

  useEffect(() => {
    if (!isOpen) {
      setStep(1);
      setSelection(null);
      setSecondsLeft(600);
      setPartySizeInput('2');
    }
  }, [isOpen]);

  useEffect(() => {
    if (step !== 2) return;
    setSecondsLeft(600);
    const id = setInterval(() => setSecondsLeft((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [step]);

  useEffect(() => {
    if (step === 2) {
      // Initialize local party size input from store
      setPartySizeInput(String(reservationStore.party_size || ''));
    }
  }, [step]);

  if (!isOpen) return null;

  const minutes = Math.floor(secondsLeft / 60);
  const seconds = (secondsLeft % 60).toString().padStart(2, '0');

  const proceedToDetails = () => {
    if (!selection) return;
    reservationStore.setFromDate(selection);
    setStep(2);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (secondsLeft === 0) return; // hold expired
    // Validate party size from local input
    const parsedPartySize = Number.parseInt(partySizeInput, 10);
    if (!Number.isFinite(parsedPartySize) || parsedPartySize < 1 || parsedPartySize > 12) {
      toast.error('Please enter a valid party size (1-12).');
      return;
    }
    // Enforce min party size on private event days (Sun/Mon)
    const sel = selection ?? new Date(`${reservationStore.date}T${reservationStore.time}:00`);
    const day = sel.getDay(); // 0=Sun, 1=Mon
    if ((day === 0 || day === 1) && parsedPartySize < 15) {
      // Surface a clear message
      toast.error('Private event days require a party size of 15 or more.');
      return;
    }
    reservationStore.setPartySize(parsedPartySize);
    await reservationStore.submit();
    if (reservationStore.success) onClose();
  };

  // Business hours (PT):
  // Tue-Sat: 4:00 PM - 9:00 PM
  // Sun-Mon: Closed for regular reservations

  const isRegularOpenDay = (d: Date) => {
    const day = d.getDay(); // 0=Sun ... 6=Sat
    return day >= 2 && day <= 6; // Tue..Sat
  };

  const getOpenCloseForDate = (d: Date) => {
    const day = d.getDay();
    const open = new Date(d);
    open.setSeconds(0, 0);
    const close = new Date(d);
    close.setSeconds(0, 0);
    if (day >= 2 && day <= 6) { // Tue-Sat
      open.setHours(16, 0, 0, 0); // 4:00 PM
      close.setHours(21, 0, 0, 0); // 9:00 PM
      return { open, close };
    }
    return null; // Sun/Mon
  };

  const filterDate = (d: Date) => {
    const isFutureOrToday = d >= new Date(new Date().setHours(0, 0, 0, 0));
    return isFutureOrToday;
  };

  const filterTime = (t: Date) => {
    // Ensure within business hours
    const bound = getOpenCloseForDate(t);
    if (!bound) {
      const now = new Date();
      const isSameDay = t.getFullYear() === now.getFullYear() && t.getMonth() === now.getMonth() && t.getDate() === now.getDate();
      const notPast = !isSameDay || t.getTime() > now.getTime();
      return notPast;
    }
    const now = new Date();
    // block past times for today
    const isSameDay = t.getFullYear() === now.getFullYear() && t.getMonth() === now.getMonth() && t.getDate() === now.getDate();
    const withinHours = t >= bound.open && t <= bound.close;
    const notPast = !isSameDay || t.getTime() > now.getTime();
    return withinHours && notPast;
  };

  const handleClose = () => {
    reservationStore.reset();
    onClose();
  };

  const isPartySizeValid = () => {
    const n = Number.parseInt(partySizeInput, 10);
    return Number.isFinite(n) && n >= 1 && n <= 12;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white text-darkBrown rounded-lg shadow-lg w-full max-w-md p-6 relative">
        <button aria-label="Close" onClick={handleClose} className="absolute right-3 top-3 text-gray-500 hover:text-gray-700 text-xl leading-none">
          &times;
        </button>
        <h2 className="text-2xl font-bold mb-4 text-center">Reserve a Table</h2>
        {step === 1 && (
          <div>
            <p className="mb-3 text-sm text-gray-600 text-center">Choose your date and time</p>
            <DatePicker
              selected={selection}
              onChange={(d) => setSelection(d as Date)}
              showTimeSelect
              minDate={new Date()}
              filterDate={filterDate}
              filterTime={filterTime}
              timeIntervals={30}
              dateFormat="MMMM d, yyyy h:mm aa"
              className="w-full border border-gray-300 p-2 rounded"
              inline
            />
            <div className="mt-2 text-xs text-gray-600 text-center">All times Pacific Time (PT)</div>
            {selection && (selection.getDay() === 0 || selection.getDay() === 1) && (
              <div className="mt-2 text-xs text-maroon text-center">
                Sunday/Monday are open for Private Events. Party size 15+ required.
              </div>
            )}
            <div className="flex justify-between mt-4">
              <button
                type="button"
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded"
                onClick={handleClose}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-maroon text-white rounded hover:bg-olive disabled:opacity-50"
                disabled={!selection}
                onClick={proceedToDetails}
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <form onSubmit={submit}>
            <div className="mb-2 text-sm text-gray-600 flex items-center justify-between">
              <span>
                Selected: {reservationStore.date} {reservationStore.time}
              </span>
              <button type="button" className="underline text-maroon" onClick={() => setStep(1)}>Change</button>
            </div>
            <div className="mb-3">
              <label className="block text-sm mb-1">Name</label>
              <input className="w-full border border-gray-300 p-2 rounded" value={reservationStore.name} onChange={(e)=>reservationStore.setName(e.target.value)} required />
            </div>
            <div className="mb-3">
              <label className="block text-sm mb-1">Email</label>
              <input type="email" className="w-full border border-gray-300 p-2 rounded" value={reservationStore.email} onChange={(e)=>reservationStore.setEmail(e.target.value)} required />
            </div>
            <div className="mb-3">
              <label className="block text-sm mb-1">Phone</label>
              <input className="w-full border border-gray-300 p-2 rounded" value={reservationStore.phone_number} onChange={(e)=>reservationStore.setPhone(e.target.value)} />
            </div>
            <div className="mb-3">
              <label className="block text-sm mb-1">Party Size</label>
              <input
                type="number"
                min={1}
                max={12}
                className="w-full border border-gray-300 p-2 rounded"
                value={partySizeInput}
                onChange={(e) => setPartySizeInput(e.target.value)}
                placeholder="e.g. 2"
              />
            </div>
            <div className="mb-3">
              <label className="block text-sm mb-1">Message (optional)</label>
              <textarea className="w-full border border-gray-300 p-2 rounded" value={reservationStore.message} onChange={(e)=>reservationStore.setMessage(e.target.value)} />
            </div>
            <div className="mb-3 text-center text-sm">
              Hold expires in <span className="font-bold">{minutes}:{seconds}</span>
            </div>
            {reservationStore.isWithin24hPT && (
              <div className="mb-4 text-sm text-red-700 bg-red-50 p-3 rounded">
                Please note: reservations requested within 24 hours cannot be guaranteed or confirmed via email. For assistance, call us at (805) 563-6311 at or after the opening hour for that day.
              </div>
            )}
            <div className="flex justify-between mt-4">
              <div>
                <button type="button" onClick={() => setStep(1)} className="px-4 py-2 bg-gray-100 text-gray-700 rounded mr-2">Back</button>
              </div>
              <div className="ml-auto">
                <button type="button" onClick={handleClose} className="px-4 py-2 bg-gray-200 text-gray-700 rounded mr-2">Cancel</button>
                <button
                  type="submit"
                  disabled={reservationStore.isSubmitting || secondsLeft===0 || !isPartySizeValid()}
                  className="px-4 py-2 bg-maroon text-white rounded hover:bg-olive disabled:opacity-50"
                >
                  {reservationStore.isSubmitting ? 'Sending...' : secondsLeft===0 ? 'Hold expired' : 'Send Request'}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
});

export default ReservationModal;
